import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import "../styles/ProductCard.css"; // kept so the shared .out-of-stock / .low-stock rules still load

export default function ProductCard({ product }) {
  // Same safety net as before: don't crash if the cart provider is missing
  const cartContext = useCart?.() || {};
  const cart = cartContext.cart ?? [];
  const addToCart = cartContext.addToCart ?? (() => {});

  const [justAdded, setJustAdded] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const stock = Number(product.stock ?? 0);
  const price = Number(product.price);
  const threshold = Number(product.low_stock_threshold ?? 5);

  const inCart = cart.find((item) => item.id === product.id)?.qty ?? 0;
  const soldOut = stock <= 0;
  const maxedOut = !soldOut && inCart >= stock;
  const lowStock = !soldOut && stock <= threshold;

  // Single product pages live at /product/:id (the category pages own /products/:category)
  const detailPath = `/product/${product.id}`;

  function handleAdd() {
    if (soldOut || maxedOut) return;

    addToCart({
      id: product.id,
      name: product.name,
      price,
      image_url: product.image_url,
    });

    setJustAdded(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setJustAdded(false), 1400);
  }

  // The hover zoom grows from wherever the cursor is over the image (see .pt-media in theme.css)
  function handleMediaMove(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    e.currentTarget.style.setProperty("--zx", `${x}%`);
    e.currentTarget.style.setProperty("--zy", `${y}%`);
  }

  let buttonLabel = "Add to cart";
  if (soldOut) buttonLabel = "Out of stock";
  else if (maxedOut) buttonLabel = "All available in cart";
  else if (justAdded) buttonLabel = "Added";

  return (
    <article className="pt-card">
      <Link
        to={detailPath}
        className="pt-media"
        aria-label={`View ${product.name}`}
        onMouseMove={handleMediaMove}
      >
        {product.image_url ? (
          <img src={product.image_url} alt={product.name} loading="lazy" />
        ) : (
          <span className="pt-placeholder" aria-hidden="true">
            {product.name?.charAt(0)}
          </span>
        )}

        {soldOut && <span className="pt-flag ui-badge ui-badge--out">Out of stock</span>}
        {lowStock && (
          <span className="pt-flag ui-badge ui-badge--low">Only {stock} left</span>
        )}
      </Link>

      <div className="pt-body">
        {product.category && <span className="pt-cat">{product.category}</span>}
        <h3 className="pt-name">
          <Link to={detailPath}>{product.name}</Link>
        </h3>
        {product.description && <p className="pt-desc">{product.description}</p>}
        <div className="pt-price">£{price.toFixed(2)}</div>
      </div>

      <div className="pt-actions">
        <Link to={detailPath} className="ui-btn ui-btn--ghost ui-btn--sm">
          View details
        </Link>
        <button
          type="button"
          className="ui-btn ui-btn--primary ui-btn--sm"
          onClick={handleAdd}
          disabled={soldOut || maxedOut}
        >
          {buttonLabel}
        </button>
      </div>
    </article>
  );
}